import { Component, OnInit, Inject, Input } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TrackerRest } from '../../../services/tracker.rest';
import { TrackerQueryService } from '../../../services/tracker.query.service';
import { UiService } from '../../../../../core/services/core.ui.service';
import { UserService } from '../../../../../core/services/core.user.service';
import { StateService } from '@uirouter/angular';
import { GfTranslateService } from '../../../../../core/services/core.translate.service';
import { TrackerQueryDeleteComponent } from '../trackerQueryDelete/tracker.query.delete.component';
import * as _ from 'lodash';

@Component({
  selector: 'tracker-query-list',
  template: require('./tracker.query.list.component.html')
})
export class TrackerQueryListComponent implements OnInit {
  @Input() project;
  @Input() projectMenu;
  @Input() tracker;

  queries: any = [];
  showEdit: boolean;
  showDelete: boolean;
  paging: any;
  filters: any;

  constructor(
    private modalService: NgbModal,
    private trackerRest: TrackerRest,
    private stateService: StateService, 
    private trackerQueryService: TrackerQueryService,
    private $window: Window,
    private uiService: UiService,
    private translateService: GfTranslateService,
    private userService: UserService
  ) {}

  ngOnInit() {
    this.showEdit = this.userService.session.hasUser();
    this.showDelete = this.userService.session.hasUser();

    this.paging = {
      page_num: _.get(this.stateService.params, 'page_num', 1),
      page_size: _.get(this.stateService.params, 'page_size', 20)
    };

    this.filters = {
      main_fields: this.setFilterValues(this.getBaseFilters(), this.stateService.params)
    };

    this.applyFilters();
  }

  updatePaging(params) {
    this.paging = params;
    this.applyFilters();
  }

  onApplyFilters() {
    this.paging.page_num = 1;
    this.applyFilters();
  }

  onResetFilters() {
    this.paging.page_num = 1;
    this.filters = {
      main_fields: this.getBaseFilters()
    };
    this.applyFilters();
  }

  newQuery(tracker_id) {
    this.stateService.go('home.project.summary.query.add', { tracker: tracker_id, queryId: 0 });
  }

  showDeleteQuery(query) {
    const modalRef = this.modalService.open(TrackerQueryDeleteComponent);
      modalRef.componentInstance.query = query;
      modalRef.componentInstance.close.subscribe(() => {
        modalRef.close();
        this.refresh();
    });
  }

  exportQuery(query, format, event: Event) {
    event.preventDefault();

    const url = this.trackerQueryService.getQueryExportUrl(query, format);
    this.$window.open(url, '_self');
  }

  preventDefault(event: Event) {
    event.preventDefault();
  }

  setShared(query, newValue) {
    if (!this.userService.session.hasUser()) return;

    if (_.get(query, 'user.id', 0) <= 100) {
      const msg = this.translateService.translate('The query "{{queryName}}" is owned by the system and cannot be set to private.', { queryName: query.queryName });
      const title = this.translateService.translate('Cannot change privacy settings');
      this.uiService.message.showError(msg, title);
      return;
    }

    let _query = {
      isShared: newValue
    };

    this.trackerRest.postQuery(query.tracker.id, query.id, _query)
      .then(() => {
        query.isShared = newValue;

        const sharedText = newValue ? this.translateService.translate('Public') : this.translateService.translate('Private');
        const msg = this.translateService.translate('{{queryName}} is now {{sharedText}}', { queryName: query.queryName, sharedText: sharedText });
        const title = this.translateService.translate('Update Query');
        this.uiService.message.showSuccess(msg, title);
      })
      .catch(response => {
        const msg = this.translateService.translate('Sorry, could not update {{queryName}}', { queryName: query.queryName });
        const title = this.translateService.translate('Update Failed');
        this.uiService.message.showError(msg, title);
      });
  }

  loadProjectQueries() {
    this.queries = null;

    this.tracker = _.find(
      _.get(this.project, 'rel.trackers', []),
      { tracker_id: _.get(this.stateService.params, 'tracker', 0) }
    );

    let params = _.pickBy(
      this.stateService.params,
      (item, key) => item && typeof item !== 'function'
    );
    params['projectId'] = this.stateService.params.unixName;

    return this.trackerRest.projectQueries(this.stateService.params.tracker, params)
      .then(response => {
        this.queries = _.sortBy(
          _.get(response, 'items', []),
          ['queryName', 'tracker.trackerName']
        );
      })
      .catch(response => {
        console.error(response);
        this.queries = [];
      });
  }

  applyFilters() {
    const newParams = {
      unixName: this.stateService.params.unixName,
      tracker: this.stateService.params.tracker,
      ...this.paging,
      ...this.getFilterValues(
        _.get(this, 'filters.main_fields', [])
      )
    };

    this.stateService.go('.', newParams, { inherit: false, notify: true, reload: false })
      .then(() => { this.loadProjectQueries(); });
  }

  refresh() {
    this.applyFilters();
  }

  getFilterValues(filters) {
    return _.reduce(
      filters,
      (carry, f) => {
        carry[f.alias] = _.get(f, 'value.id', _.get(f, 'value', ''));
        return carry;
      },
      {}
    );
  }

  getBaseFilters() {
    const anyTracker = { id: 0, label: this.translateService.translate('Any') };
    const trackers = _.concat(anyTracker, _.map(this.project.rel.trackers, tracker => {
      return { id: tracker.tracker_id, label: tracker.tracker_name };
    }));

    const visibility = [
      { id: '', label: this.translateService.translate('Any') },
      { id: 'true', label: this.translateService.translate('Public') },
      { id: 'false', label: this.translateService.translate('Private') }
    ];

    return [
      { alias: 'queryName', fieldName: this.translateService.translate('Text search'), value: '', fieldType: 4, isExtraField: false },
      { alias: 'tracker', fieldName: this.translateService.translate('Tracker'), value: anyTracker, fieldType: 1, isExtraField: true, fieldElements: trackers },
      { alias: 'isShared', fieldName: this.translateService.translate('Visibility'), value: visibility[0], fieldType: 1, isExtraField: true, fieldElements: visibility }
    ];
  }

  setFilterValues(filters, params) {
    return _.map(
      filters,
      f => {
        const value = _.get(params, f.alias, '');
        const valObject = _.find(
          _.get(f, 'fieldElements', []),
          item => item.id == value
        );
        _.set(f, 'value', valObject ? valObject : value);
        return f;
      }
    );
  }
}
